import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, Check, Users, Camera, CalendarHeart, Filter } from "lucide-react";
import { formatRupiah as formatIDR } from "../lib/format";
import Seo from "../components/Seo";
import Reveal from "../components/Reveal";
import PublicNav from "../components/PublicNav";
import PublicFooter from "../components/PublicFooter";
import { getVendorBySlug } from "../lib/vendors";

const publicApi = axios.create({ baseURL: process.env.REACT_APP_BACKEND_URL + "/api" });
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

const FALLBACK_IMG =
  "https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=1200&q=80";

const STORAGE_KEY_PREFIX = "dwg_vendor_state_";

export default function VendorDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const vendor = useMemo(() => getVendorBySlug(slug), [slug]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cmsHero, setCmsHero] = useState(null);
  const storageKey = `${STORAGE_KEY_PREFIX}${slug}`;
  const restored = useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem(storageKey) || "{}"); }
    catch { return {}; }
  }, [storageKey]);
  const [cat, setCat] = useState(restored.cat || "all");

  useEffect(() => {
    if (!vendor) return;
    setLoading(true);
    publicApi
      .get("/public/catalog", {
        params: { division: vendor.division_id, bundling: false },
      })
      .then(({ data }) => setItems(data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
    // Load CMS to fetch vendor hero image override
    publicApi.get("/public/cms/landing")
      .then(({ data }) => {
        const v = data?.vendor_images_resolved?.[slug];
        if (v?.url) setCmsHero(`${BACKEND_URL}${v.url}`);
      })
      .catch(() => {});
  }, [vendor?.division_id, slug]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Preserve filter + scroll for back-navigation
  useEffect(() => {
    const save = () => {
      sessionStorage.setItem(storageKey, JSON.stringify({ cat, scrollY: window.scrollY }));
    };
    window.addEventListener("beforeunload", save);
    return () => { save(); window.removeEventListener("beforeunload", save); };
  }, [cat, storageKey]);

  useEffect(() => {
    if (!loading && restored.scrollY) {
      window.scrollTo({ top: restored.scrollY, behavior: "instant" });
    }
  }, [loading]);  // eslint-disable-line react-hooks/exhaustive-deps

  const categoryOptions = useMemo(() => {
    const set = new Set();
    items.forEach((p) => p.event_category && set.add(p.event_category));
    return ["all", ...Array.from(set)];
  }, [items]);

  const filtered = useMemo(
    () => (cat === "all" ? items : items.filter((p) => p.event_category === cat)),
    [items, cat]
  );

  if (!vendor) {
    return (
      <div className="min-h-screen bg-[#F7F1E8] text-[#1E1611] w-full max-w-full overflow-x-hidden" style={{ fontFamily: "'Inter', sans-serif" }} data-public-page="true">
        <PublicNav />
        <div className="max-w-3xl mx-auto px-4 py-24 text-center">
          <h1 className="font-serif text-3xl">Vendor tidak ditemukan</h1>
          <p className="mt-3 text-[#5C4A3D]">Silakan kembali ke daftar semua vendor.</p>
          <Link
            to="/vendor"
            className="mt-6 inline-flex items-center gap-2 bg-[#1E1611] text-white px-6 py-3 rounded-full text-xs uppercase tracking-widest"
            data-testid="vendor-not-found-back"
          >
            <ArrowLeft className="w-4 h-4" /> Semua Vendor
          </Link>
        </div>
        <PublicFooter />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-[#F7F1E8] text-[#1E1611] w-full max-w-full overflow-x-hidden"
      style={{ fontFamily: "'Inter', sans-serif" }}
      data-testid="vendor-detail-page"
      data-public-page="true"
    >
      <style>{`
        .font-serif { font-family: 'Cormorant Garamond', serif; }
        .gold-line { background: linear-gradient(90deg, transparent 0%, #C4A57B 20%, #C4A57B 80%, transparent 100%); height: 1px; }
        .luxury-shadow { box-shadow: 0 20px 60px -20px rgba(139, 90, 42, 0.35); }
      `}</style>

      <Seo title={`${vendor.name} — ${vendor.tagline}`} path={`/vendor/${slug}`} image={cmsHero || vendor.image}
        description={`${vendor.name} by Dessy Wedding Group: ${vendor.description}`.slice(0, 160)} />
      <PublicNav />

      {/* Back to all vendors */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6">
        <button
          onClick={() => nav("/vendor")}
          className="inline-flex items-center gap-1.5 text-xs text-[#5C4A3D] hover:text-[#8B5A2A] font-medium"
          data-testid="vendor-back-all"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Semua Vendor
        </button>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#E5D9C4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 md:py-16 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#8B5A2A] font-semibold mb-3">
              {vendor.tagline}
            </div>
            <h1 className="font-serif text-[36px] md:text-[54px] leading-[1.05] tracking-tight">
              {vendor.name}
            </h1>
            <div className="mt-4 gold-line max-w-xs" />
            <p className="mt-5 text-[#5C4A3D] font-light text-sm md:text-base max-w-lg">
              {vendor.description}
            </p>
            {!loading && (
              <div className="mt-6 text-xs text-[#8B5A2A] uppercase tracking-widest font-semibold">
                {items.length} paket tersedia
              </div>
            )}
          </div>
          <div className="relative">
            <img
              src={cmsHero || vendor.image}
              alt={vendor.name}
              className="w-full h-64 md:h-80 object-cover luxury-shadow rounded-tl-[40px] rounded-br-[40px]"
              loading="eager"
              data-testid="vendor-hero-image"
            />
          </div>
        </div>
      </section>

      {/* Filter chips (event category) */}
      {categoryOptions.length > 2 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-8">
          <div className="flex gap-2 flex-wrap items-center">
            <Filter className="w-3.5 h-3.5 text-[#8B5A2A]" />
            <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#5C4A3D]">
              Jenis acara:
            </span>
            {categoryOptions.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                data-testid={`vd-cat-${c.toLowerCase()}`}
                className={`px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.15em] font-semibold transition-all ${
                  cat === c
                    ? "bg-[#1E1611] text-[#F7F1E8]"
                    : "bg-white border border-[#E5D9C4] text-[#5C4A3D] hover:border-[#8B5A2A]"
                }`}
              >
                {c === "all" ? "Semua" : c}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-8 pb-16">
        {loading && (
          <div className="text-center py-16 text-[#8B5A2A] font-serif italic text-lg">
            Memuat paket vendor...
          </div>
        )}
        {!loading && items.length === 0 && <EmptyVendor vendor={vendor} nav={nav} />}
        {!loading && items.length > 0 && filtered.length === 0 && (
          <div className="text-center py-16 text-[#8B5A2A]">
            <p className="font-serif italic text-lg">
              Tidak ada paket untuk kategori ini.
            </p>
            <button
              onClick={() => setCat("all")}
              className="mt-3 text-xs uppercase tracking-widest underline"
              data-testid="vd-reset"
            >
              Tampilkan semua kategori
            </button>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((p, i) => (
            <Reveal key={p.product_id} delay={(i % 3) * 0.08}>
              <VendorPackageCard
                p={p}
                onOpen={() =>
                  nav(`/catalog/${p.product_id}?from=vendor:${slug}`)
                }
              />
            </Reveal>
          ))}
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}

function EmptyVendor({ vendor, nav }) {
  return (
    <div className="text-center py-16 bg-white rounded-2xl border border-[#E5D9C4]" data-testid="vendor-empty-state">
      <CalendarHeart className="w-10 h-10 text-[#8B5A2A] mx-auto mb-3" />
      <h3 className="font-serif text-2xl text-[#1E1611]">
        Paket {vendor.name} akan segera hadir
      </h3>
      <p className="mt-2 text-[#5C4A3D] font-light max-w-md mx-auto text-sm">
        Kami sedang menyiapkan katalog paket & harga untuk vendor ini. Sementara
        itu, silakan konsultasi langsung agar tim kami dapat menyusun penawaran
        khusus untuk kebutuhan Anda.
      </p>
      <button
        onClick={() => nav(`/consultation?vendor=${encodeURIComponent(vendor.name)}`)}
        className="mt-6 inline-flex items-center gap-2 bg-[#1E1611] text-white hover:bg-[#8B5A2A] transition-colors px-6 py-3 rounded-full text-xs uppercase tracking-widest font-semibold"
        data-testid="vendor-empty-cta-consult"
      >
        <CalendarHeart className="w-4 h-4" /> Konsultasi Vendor Ini
      </button>
    </div>
  );
}

function VendorPackageCard({ p, onOpen }) {
  const hasPromo = p.promo_active && p.promo_price > 0;
  return (
    <button
      onClick={onOpen}
      className="text-left bg-white rounded-2xl overflow-hidden luxury-shadow hover:-translate-y-1 transition-all duration-500 group w-full flex flex-col h-full"
      data-testid={`vd-card-${p.product_id}`}
    >
      <div className="relative h-52 overflow-hidden">
        <img
          src={resolveImg(p.card_image) || FALLBACK_IMG}
          alt={p.product_name}
          className="w-full h-full group-hover:scale-110 transition-transform duration-700 object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
        <div className="absolute top-3 left-3 flex flex-col gap-1">
          {p.event_category && (
            <span className="text-[9px] uppercase tracking-[0.2em] bg-white/95 text-[#1E1611] px-3 py-1.5 rounded-full font-semibold">
              {p.event_category}
            </span>
          )}
          {hasPromo && (
            <span className="text-[9px] uppercase tracking-[0.2em] bg-rose-600 text-white px-3 py-1.5 rounded-full font-semibold">
              Promo
            </span>
          )}
        </div>
        <div className="absolute bottom-3 left-4 right-4 text-white">
          <div className="font-serif text-2xl leading-tight">{p.product_name}</div>
        </div>
      </div>
      <div className="p-5 flex-1 flex flex-col">
        {p.short_description && (
          <p className="text-xs text-[#5C4A3D] line-clamp-2 min-h-[32px] font-light">
            {p.short_description}
          </p>
        )}
        {p.included_items?.length > 0 && (
          <div className="mt-3 space-y-1">
            {p.included_items.slice(0, 4).map((it, i) => (
              <div key={i} className="flex items-start gap-1.5 text-[11px] text-[#3A2E28]">
                <Check className="w-3 h-3 text-[#8B5A2A] shrink-0 mt-0.5" />
                <span className="line-clamp-1">{it}</span>
              </div>
            ))}
            {p.included_items.length > 4 && (
              <div className="text-[10px] text-[#8B5A2A]">
                +{p.included_items.length - 4} lainnya
              </div>
            )}
          </div>
        )}
        {(p.guest_capacity > 0 || p.crew_count > 0) && (
          <div className="mt-3 flex gap-3 text-[10px] text-[#8B5A2A]">
            {p.guest_capacity > 0 && (
              <span className="flex items-center gap-1">
                <Users className="w-3 h-3" /> Hingga {p.guest_capacity} tamu
              </span>
            )}
            {p.crew_count > 0 && (
              <span className="flex items-center gap-1">
                <Camera className="w-3 h-3" /> {p.crew_count} crew
              </span>
            )}
          </div>
        )}
        <div className="mt-5 pt-4 border-t border-[#F0EBE0] flex items-end justify-between mt-auto">
          <div>
            <div className="text-[9px] uppercase tracking-[0.2em] text-[#8B5A2A] font-semibold">
              {hasPromo ? "Harga promo" : "Harga paket"}
            </div>
            {hasPromo ? (
              <>
                <div className="text-[11px] text-[#8C7A5C] line-through">
                  {formatIDR(p.normal_price)}
                </div>
                <div className="font-serif font-medium text-rose-700 text-2xl">
                  {formatIDR(p.promo_price)}
                </div>
              </>
            ) : (
              <div className="font-serif font-medium text-[#1E1611] text-2xl">
                {formatIDR(p.normal_price)}
              </div>
            )}
          </div>
          <span className="text-[10px] uppercase tracking-widest text-[#8B5A2A] group-hover:translate-x-1 transition-transform inline-flex items-center gap-1 font-semibold">
            Detail <ArrowRight className="w-3 h-3" />
          </span>
        </div>
      </div>
    </button>
  );
}
